import SEO from '@/components/SEO';
import { site } from '@/data/content';
import {
  Heart,
  Users,
  Globe,
  Shield,
  Sparkles,
  Hand,
  ArrowUpRight,
  TreePine,
  GraduationCap,
  Gift,
  Building2,
  MessageCircle,
  Award,
  Target,
  Zap,
  Leaf,
} from 'lucide-react';

const cultureValues = [
  {
    icon: Heart,
    title: 'People-first culture',
    text: 'We build our company around people. Every decision — from product features to team policies — starts with asking: How does this serve humans?',
  },
  {
    icon: Building2,
    title: 'How we work together',
    text: 'We believe in transparency, open communication, and shared ownership. Every team member has a voice, and we celebrate diverse perspectives as our strength.',
  },
  {
    icon: MessageCircle,
    title: 'Respectful communication',
    text: 'We speak with kindness, listen with intention, and resolve conflicts constructively. Clear, honest communication builds trust and belonging.',
  },
  {
    icon: Shield,
    title: 'Psychological safety',
    text: 'We create environments where people feel safe to speak up, share ideas, admit mistakes, and bring their authentic selves to work.',
  },
];

const employeePrinciples = [
  {
    icon: Users,
    title: 'Equitable opportunities',
    text: 'Fair compensation, growth paths, and recognition. We ensure equal access to opportunities regardless of background, identity, or location.',
  },
  {
    icon: Globe,
    title: 'Distributed-first hiring',
    text: 'We hire talent globally, not just from major cities. Remote-first work means access to opportunities for people everywhere.',
  },
  {
    icon: Sparkles,
    title: 'Continuous learning',
    text: 'We invest in growth through mentorship, training budgets, and knowledge-sharing. Learning is part of the job, not an add-on.',
  },
  {
    icon: Hand,
    title: 'Work-life integration',
    text: 'Flexible schedules, generous leave, and respect for personal time. We treat team members as whole people, not just workers.',
  },
];

const customerCommitments = [
  {
    icon: Target,
    title: 'Accessible by design',
    text: 'We follow WCAG guidelines, test with screen readers, and ensure keyboard navigation. Our product works for everyone.',
  },
  {
    icon: Award,
    title: 'Fair AI practices',
    text: 'Zyra AI is reviewed for bias. We avoid exclusionary language and let teams customize tone for their audience.',
  },
  {
    icon: Zap,
    title: 'Inclusive pricing',
    text: 'Transparent pricing, no hidden fees, and plans that work for startups and enterprises alike. Fair access for teams of all sizes.',
  },
  {
    icon: Shield,
    title: 'Data privacy first',
    text: 'We protect customer data with enterprise-grade security. Your data belongs to you — we never sell it.',
  },
];

const oneThingInitiatives = [
  {
    icon: TreePine,
    title: 'Planting trees',
    text: 'For every new ZuriHub workspace, we plant trees in partnership with global reforestation projects. Building software that gives back to the planet.',
  },
  {
    icon: GraduationCap,
    title: 'Tech scholarship program',
    text: 'We sponsor aspiring technologists from underserved communities, providing education, mentorship, and pathways to tech careers.',
  },
  {
    icon: Gift,
    title: 'Community donations',
    text: 'Through 1thing by Zuri — our collection campaign across ZuriHub, ZuriChat, and Zurisend — we donate to local communities and causes that matter.',
  },
];

const culturePillars = [
  {
    title: 'Ownership & accountability',
    text: 'We take responsibility for our work, learn from mistakes, and celebrate wins together. Trust is built through consistent action.',
  },
  {
    title: 'Radical candor',
    text: 'We give feedback directly and receive it openly. Growth happens when we challenge each other with respect and care.',
  },
  {
    title: 'Customer obsession',
    text: 'Every feature decision starts with the customer. We listen deeply, respond quickly, and never stop improving.',
  },
  {
    title: 'Sustainable growth',
    text: 'We build for the long term — financially, environmentally, and socially. Profit matters, but not at the expense of people or planet.',
  },
];

const stats = [
  { label: 'Team members across countries', value: '12+' },
  { label: 'Trees planted to date', value: '5,000+' },
  { label: 'Tech scholars sponsored', value: '50+' },
  { label: 'Community donations made', value: '25+' },
];

export default function Inclusion() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'Inclusion & Belonging',
    description: 'Learn about ZuriHub culture, values, employee principles, customer commitments, and the 1thing Initiative for social impact.',
    url: `${site.baseUrl}/inclusion`,
    mainEntity: {
      '@type': 'Organization',
      name: 'ZuriHub',
      description: 'Customer support platform built on inclusion, belonging, and social responsibility.',
    },
  };

  return (
    <>
      <SEO
        title="Inclusion & Belonging"
        description="ZuriHub culture is built on people-first values, equitable opportunities, and social impact through the 1thing Initiative — planting trees, sponsoring tech scholars, and donating to communities."
        path="/inclusion"
        keywords="company culture, inclusion, diversity, belonging, social impact, CSR, tech scholarships, tree planting, corporate responsibility"
        jsonLd={[jsonLd]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="text-center lg:text-left">
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-3 py-1 text-sm font-semibold text-brand-300">
                <Heart className="h-4 w-4" /> Our culture
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Inclusion & <span className="gradient-text">Belonging</span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300 lg:mx-0">
                We build a company where everyone belongs — from how we work together, to how we treat customers, to how we give back through the 1thing Initiative.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
                >
                  Join our team <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#1thing"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/60 px-7 py-3.5 text-base font-bold text-slate-200 transition hover:bg-slate-800"
                >
                  Our impact
                </a>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-900/60 p-10 text-center backdrop-blur">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br from-brand-500 to-brand-700 shadow-2xl shadow-brand-900/50">
                <Heart className="h-12 w-12 text-white" />
              </div>
              <h2 className="mt-6 text-2xl font-bold text-white">People First</h2>
              <p className="mt-2 text-slate-300">Our foundation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Culture Values */}
      <section className="py-24" aria-labelledby="culture-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="culture-heading" className="section-title">Our Culture & Values</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              Inclusion isn't a policy — it's how we exist. These values guide how we work, collaborate, and build.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {cultureValues.map((v) => (
              <div
                key={v.title}
                className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-900/20 transition group-hover:scale-110">
                  <v.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{v.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Pillars */}
      <section className="bg-slate-50 py-24" aria-labelledby="pillars-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="pillars-heading" className="section-title">How We Work Together</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              These pillars define our daily interactions, decision-making, and collective success.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {culturePillars.map((p, i) => (
              <div
                key={p.title}
                className="rounded-3xl border border-slate-200 bg-white p-8 shadow-lg"
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-brand-700 font-bold">
                  {i + 1}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{p.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Employee Principles */}
      <section className="py-24" aria-labelledby="employees-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="employees-heading" className="section-title">How We Treat Our Team</h2>
              <p className="mt-4 text-lg text-slate-600">
                We build the kind of workplace we'd want to work in. Fairness, growth, and respect aren't optional — they're non-negotiable.
              </p>
              <div className="mt-8 space-y-6">
                {employeePrinciples.map((p) => (
                  <div key={p.title} className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                      <p.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{p.title}</h3>
                      <p className="mt-1 text-slate-600">{p.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 p-10 text-white shadow-2xl">
              <h3 className="text-2xl font-bold">Join a team that values you</h3>
              <p className="mt-4 text-brand-100">
                We're always looking for people who care deeply about building inclusive products and workplaces.
              </p>
              <a
                href="/book-a-demo"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-base font-bold text-brand-700 transition hover:bg-slate-100"
              >
                View open roles <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Commitments */}
      <section className="bg-slate-900 py-24 text-white" aria-labelledby="customers-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="customers-heading" className="section-title">How We Treat Our Customers</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl text-slate-300">
              Inclusion extends to everyone who uses ZuriHub. Our product and policies reflect our commitment to fairness.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {customerCommitments.map((c) => (
              <div
                key={c.title}
                className="rounded-3xl border border-slate-700 bg-slate-800 p-8 transition hover:bg-slate-750"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500/20 text-brand-400">
                  <c.icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-bold text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-300">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 1thing Initiative */}
      <section id="1thing" className="py-24" aria-labelledby="onething-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-green-500/30 bg-green-500/10 px-3 py-1 text-sm font-semibold text-green-300">
              <Leaf className="h-4 w-4" /> Social impact
            </span>
            <h2 id="onething-heading" className="mt-6 section-title">The 1thing Initiative</h2>
            <p className="section-subtitle mx-auto mt-4 max-w-2xl">
              1thing by Zuri is our collection campaign across ZuriHub, ZuriChat, and Zurisend. One action, multiplied across our products, creates real impact.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-3">
            {oneThingInitiatives.map((i) => (
              <div
                key={i.title}
                className="group rounded-3xl border border-slate-100 bg-white p-8 shadow-xl shadow-brand-900/5 transition duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand-900/10"
              >
                <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-green-500 to-green-700 text-white shadow-lg shadow-green-900/20 transition group-hover:scale-110">
                  <i.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{i.title}</h3>
                <p className="mt-3 text-slate-600">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 py-24 text-white" aria-labelledby="impact-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 id="impact-heading" className="text-center text-3xl font-extrabold tracking-tight sm:text-4xl">
            Our Impact So Far
          </h2>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-3xl border border-white/20 bg-white/10 p-8 text-center backdrop-blur">
                <p className="text-4xl font-extrabold text-white">{s.value}</p>
                <p className="mt-2 text-green-100">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Be Part of Our Mission</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Whether you're joining our team, becoming a customer, or partnering with us, you're part of building a more inclusive tech industry.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="/book-a-demo"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
            >
              Join the team <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="/pricing"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-8 py-3.5 text-base font-bold text-white transition hover:bg-white/20"
            >
              Get started
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
